import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { useInquiry } from '../../context/InquiryContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useCompare } from '../../context/CompareContext';
import { getPlanConfig, shouldShowVerifiedBadge, canSendRFQ } from '../../utils/membershipUtils';
import { API_BASE_URL } from '../../config/api';
import { appendMobileBrowsingProduct } from '../../utils/mobileBrowsingHistory';
import { getDeliverLocal } from '../../utils/mobileSettingsStorage';
import { productFavoritesApi } from '../../services/apiService';
import MobileProductVariationsModal from '../MobileProductDetailsPage/MobileProductVariationsModal';
import Pagination from '../../components/Pagination/Pagination';
import './ProductDetailsPage.css';

const LOGO_PH = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=120';

const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, planName, planDisplayName, canViewContactsForRegion, getFeature, loading: subscriptionLoading } = useSubscription();
  const { isAuthenticated, user } = useAuth();
  const { addToInquiry, isInInquiry } = useInquiry();
  const { formatPrice, formatPriceRange, selectedCurrency } = useCurrency();
  const { addToCompare, removeFromCompare, isInCompare, isFull } = useCompare();
  const [activeTab, setActiveTab] = useState('productDetails');
  const [activeSubTab, setActiveSubTab] = useState('details');
  const [mainImage, setMainImage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [otherSuppliers, setOtherSuppliers] = useState([]);
  const [showMobile, setShowMobile] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [variationsOpen, setVariationsOpen] = useState(false);
  const [addedProducts, setAddedProducts] = useState({});
  const [inquiryForm, setInquiryForm] = useState({ email: '', mobile: '', message: '' });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFav, setIsTogglingFav] = useState(false);
  const [favToast, setFavToast] = useState(null);
  const favToastTimer = useRef(null);

  const showFavToast = (msg, type = 'success') => {
    setFavToast({ msg, type });
    if (favToastTimer.current) clearTimeout(favToastTimer.current);
    favToastTimer.current = setTimeout(() => setFavToast(null), 2500);
  };

  useEffect(() => () => { if (favToastTimer.current) clearTimeout(favToastTimer.current); }, []);
  const inCompare = product ? isInCompare(product.id) : false;

  const canViewSupplierContact = (sellerData) => {
    if (!hasPermission('canViewBuyerContacts')) return false;
    const supplierCountry = sellerData?.country || '';
    const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
    if (contactRegion === 'all' || contactRegion === 'global') return true;
    if (contactRegion === 'gcc') return canViewContactsForRegion ? canViewContactsForRegion(supplierCountry) : false;
    return true;
  };

  const getPhoneDisplay = (sellerData) => {
    const actualPhone = sellerData?.phone || sellerData?.phoneNumber || sellerData?.mobile;
    if (!actualPhone) return 'N/A';
    if (canViewSupplierContact(sellerData)) return actualPhone;
    return '***-***-****';
  };

  const handleChatWithSupplier = () => {
    if (!isAuthenticated) { setShowLoginModal(true); return; }
    if (subscriptionLoading) return;
    if (!hasPermission('canLiveChat')) {
      setUpgradeFeature('Live Chat');
      setUpgradeMessage('');
      setShowUpgradeModal(true);
      return;
    }
    setIsChatModalOpen(true);
  };

  const handleViewMobile = () => {
    if (!isAuthenticated) { setShowLoginModal(true); return; }
    if (subscriptionLoading) return;
    const s = product?.seller || product;
    if (!canViewSupplierContact(s)) {
      const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
      if (contactRegion === 'gcc') {
        setUpgradeFeature('View Mobile Number');
        setUpgradeMessage('This supplier is outside the GCC region. Upgrade to Global Enterprise to view mobile numbers for all suppliers worldwide.');
      } else {
        setUpgradeFeature('View Mobile Number');
        setUpgradeMessage('Upgrade to GCC Business to view supplier mobile numbers in GCC region, or Global Enterprise for worldwide access.');
      }
      setShowUpgradeModal(true);
      return;
    }
    setShowMobile(!showMobile);
  };

  const handleRequestInquire = () => {
    if (!isAuthenticated) { setShowLoginModal(true); return; }
    if (!canSendRFQ(planName)) {
      setUpgradeFeature('Send RFQ');
      setUpgradeMessage('You cannot send RFQs. Please check your account permissions.');
      setShowUpgradeModal(true);
      return;
    }
    navigate(`/send-inquire/${product?.id}`, {
      state: {
        product: {
          id: product?.id, image: mainImage, name: product?.name || product?.title,
          description: product?.name || product?.title, priceMin: product?.priceMin,
          priceMax: product?.priceMax, unit: product?.unit || product?.pricePer,
          moq: product?.moq, sellerId: product?.sellerId, userId: product?.userId,
          seller: product?.seller, user: product?.user
        },
        seller: {
          id: seller?.id, userId: seller?.userId || product?.userId,
          name: seller?.name || seller?.companyName, logo: seller?.logo,
          type: seller?.isGlobal ? 'Global' : 'Local', verified: seller?.isVerified,
          country: seller?.country, memberYears: seller?.memberYears,
          businessType: seller?.businessType
        }
      }
    });
  };

  const inquiryPayload = useCallback(() => {
    if (!product) return null;
    const s = product.seller || product.supplier || {};
    const img = mainImage || product.image || LOGO_PH;
    return {
      id: product.id,
      image: img,
      description: product.description || product.name || product.title,
      priceMin: product.priceMin,
      priceMax: product.priceMax,
      priceUnit: selectedCurrency?.code || 'USD',
      pricePer: product.unit || product.pricePer || 'Unit',
      moq: product.moq || '1 Unit',
      companyName: s.name || s.companyName || 'Supplier',
      sellerId: s.userId || product.sellerId || product.userId,
      sellerName: s.name || s.companyName,
      sellerAvatar: s.logo || LOGO_PH,
      country: s.country || 'UAE',
      countryFlag: s.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
  }, [product, mainImage, selectedCurrency]);

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setVariationsOpen(true);
  };

  const handleVariationsConfirm = useCallback(
    (opts) => {
      const pl = inquiryPayload();
      if (!pl) return;
      addToInquiry({
        ...pl,
        quantity: opts.quantity,
        variationColor: opts.color,
        variationShipping: opts.shipping,
        estimatedLineUsd: opts.lineTotalUsd
      });
    },
    [addToInquiry, inquiryPayload]
  );

  const deliverCode = getDeliverLocal()?.countryCode || 'IN';

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/products/${id}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          appendMobileBrowsingProduct(data);
          setNotFound(false);
          let productImages = [];
          if (data.images) {
            try { productImages = typeof data.images === 'string' ? JSON.parse(data.images) : data.images; } catch (e) {}
          }
          if (productImages && productImages.length > 0 && productImages[0]) {
            setMainImage(productImages[0]);
          } else if (data.image) {
            setMainImage(data.image);
          }
          fetchRelatedProducts(id);
          fetchOtherSuppliers(id);
        } else if (response.status === 404) {
          setNotFound(true);
          setProduct(null);
        } else {
          setProduct(getDefaultProduct());
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(getDefaultProduct());
      } finally {
        setLoading(false);
      }
    };
    if (id) { fetchProduct(); } else { setProduct(getDefaultProduct()); setLoading(false); }
    window.scrollTo(0, 0);
    setVariationsOpen(false);
  }, [id]);

  const fetchRelatedProducts = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/related?limit=50`);
      if (response.ok) { const data = await response.json(); setRelatedProducts((data || []).filter(p => p.status === 'published')); }
    } catch (error) { console.error('Error fetching related products:', error); }
  };

  const fetchOtherSuppliers = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/other-suppliers?limit=10`);
      if (response.ok) { const data = await response.json(); setOtherSuppliers((data || []).filter(p => p.status === 'published')); }
    } catch (error) { console.error('Error fetching other suppliers:', error); }
  };

  const getDefaultProduct = () => ({
    name: 'Product Details',
    priceMin: 0, priceMax: 0, priceUnit: selectedCurrency.code, unit: 'Unit', moq: '1 Unit',
    description: 'Product description not available.',
    image: '/uploads/builder-images/cc275acecdde1b7682591d397cc142f71b5d3f92_w800.jpg',
    seller: {
      name: 'Supplier', companyName: 'Supplier', logo: '/logo.png',
      reviews: 0, products: 0, isGlobal: false, isVerified: false,
      country: 'Unknown', memberYears: 1, businessType: 'Manufacturer', address: ''
    }
  });

  const getProductImages = () => {
    // Priority: productImages table > images JSON > single image
    if (product?.productImages && product.productImages.length > 0) {
      return product.productImages.map(pi => pi.url).filter(Boolean);
    }
    let parsedImages = [];
    if (product?.images) {
      try { parsedImages = typeof product.images === 'string' ? JSON.parse(product.images) : product.images; } catch (e) {}
    }
    if (parsedImages && parsedImages.length > 0) return parsedImages.filter(Boolean);
    const mainImg = product?.image || '/uploads/builder-images/1c71bdd709a90ef879ec030e5f88229d06ab1391_w800.png';
    return [mainImg];
  };

  const thumbnails = getProductImages();

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrev = () => setLightboxIndex(i => (i > 0 ? i - 1 : thumbnails.length - 1));
  const lightboxNext = () => setLightboxIndex(i => (i < thumbnails.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') lightboxPrev();
      if (e.key === 'ArrowRight') lightboxNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, thumbnails.length]);

  // Check if product is in user's favourites
  useEffect(() => {
    const checkFavourite = async () => {
      if (user?.id && product?.id) {
        try {
          const response = await productFavoritesApi.checkFavorite(user.id, product.id);
          setIsFavorited(response.isFavorited);
        } catch (err) {
          console.error('Error checking product favourite:', err);
        }
      }
    };
    checkFavourite();
  }, [user?.id, product?.id]);

  // Toggle product favourite
  const handleToggleFavourite = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (!product?.id || isTogglingFav) return;

    // Optimistic update
    const prev = isFavorited;
    setIsFavorited(!prev);
    setIsTogglingFav(true);

    try {
      const response = await productFavoritesApi.toggle(user.id, product.id);
      setIsFavorited(response.isFavorited);
      showFavToast(response.isFavorited ? 'Added to favourites' : 'Removed from favourites');
    } catch (err) {
      setIsFavorited(prev);
      showFavToast('Could not update favourites. Try again.', 'error');
      console.error('Error toggling product favourite:', err);
    } finally {
      setIsTogglingFav(false);
    }
  };
  const seller = product?.seller || product?.supplier || getDefaultProduct().seller;
  const reviews = (seller?.sellerReviews || []).map(r => ({
    id: r.id, name: r.reviewerName || 'Anonymous',
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(r.reviewerName || 'A')}&background=random&size=48`,
    location: r.reviewerCompany || '', rating: r.rating || 5,
    date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
    comment: r.comment || ''
  }));
  const reviewCount = reviews.length;

  const productSpecs = [
    { label: 'Color', value: product?.color },
    { label: 'Size', value: product?.size },
    { label: 'Shape', value: product?.shape },
    { label: 'Net Weight', value: product?.netWeight },
    { label: 'Material', value: product?.material },
    { label: 'Thickness', value: product?.thickness },
    { label: 'Available Size Range', value: product?.sizeRange },
    { label: 'Type of Packing', value: product?.packingType },
    { label: 'Pcs Per Inner', value: product?.pcsPerInner },
    { label: 'Size Inner', value: product?.sizeInner },
    { label: 'Type of Inner Packing', value: product?.innerPackingType },
    { label: 'Outer Per Inner', value: product?.outerPerInner },
    { label: 'Type of Outer Packing', value: product?.outerPackingType },
    { label: 'Size Outer', value: product?.sizeOuter },
    { label: 'Master Per Pallet', value: product?.masterPerPallet },
    { label: 'Type of Pallet', value: product?.palletType },
    { label: 'After Sales Support', value: product?.afterSalesSupport },
    { label: 'Incoterms', value: product?.incoTerms },
    { label: 'Payment Options', value: product?.paymentOptions },
  ].filter(spec => spec.value && spec.value.trim && spec.value.trim() !== '' && spec.value !== '-');

  // Parse and add custom specs
  let parsedCustomSpecs = [];
  try {
    const raw = product?.customSpecs;
    if (raw) {
      parsedCustomSpecs = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsedCustomSpecs)) {
        parsedCustomSpecs = parsedCustomSpecs.filter(s => s.key && s.value);
      } else {
        parsedCustomSpecs = [];
      }
    }
  } catch (e) {
    parsedCustomSpecs = [];
  }
  const allSpecs = [...productSpecs, ...parsedCustomSpecs.map(s => ({ label: s.key, value: s.value }))];

  const renderSupplierSidebar = () => (
    <div className="pd-sidebar">
      {/* Contact Supplier Box */}
      <div className="pd-contact-box">
        <p className="pd-contact-text">For more detailed information including pricing, customization, and shipping:</p>
        <button className="pd-contact-supplier-btn" onClick={handleRequestInquire}>CONTACT SUPPLIER</button>
        <button className="pd-chat-seller-btn" onClick={handleChatWithSupplier}>
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M14 0H2C0.9 0 0 0.9 0 2V10C0 11.1 0.9 12 2 12H4V15.5L9.5 12H14C15.1 12 16 11.1 16 10V2C16 0.9 15.1 0 14 0ZM14 10H9L6 12V10H2V2H14V10Z" fill="#1F396C"/></svg>
          CHAT WITH SELLER
        </button>
      </div>

      {/* Supplier Company Card */}
      <div className="pd-supplier-card">
        <Link to={`/seller/${seller.slug || seller.id}`} className="pd-supplier-name-link">
          {seller.name || seller.companyName}
        </Link>
        <p className="pd-supplier-type">Multispecialty supplier</p>
        <div className="pd-supplier-badges">
          <span className="pd-supplier-country">
            <img src="/uploads/builder-images/417568b6eb0fd8d1e09618ea0296738b87ee2e2a_w62.png" alt="" className="pd-flag-icon" />
            {(seller.country || '').substring(0, 2)}
          </span>
          <span className="pd-supplier-years">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 0L8.5 4.5H13L9.5 7.5L10.5 12L7 9.5L3.5 12L4.5 7.5L1 4.5H5.5L7 0Z" fill="#FF8C00"/></svg>
            {seller.memberYears || 1} YRS
          </span>
          <span className="pd-supplier-free-badge">Free Supplier</span>
        </div>
        <div className="pd-supplier-stats-grid">
          <div className="pd-stat-item">
            <span className="pd-stat-label">Store Rating</span>
            <span className="pd-stat-value">0/5</span>
          </div>
          <div className="pd-stat-item">
            <span className="pd-stat-label">Total Annual Revenue</span>
            <span className="pd-stat-value">$5 Million - $10 ...</span>
          </div>
          <div className="pd-stat-item">
            <span className="pd-stat-label">Year Company Registered</span>
            <span className="pd-stat-value">{seller.yearRegistered || '1990'}</span>
          </div>
          <div className="pd-stat-item">
            <span className="pd-stat-label">Export Percentage</span>
            <span className="pd-stat-value">51% - 60%</span>
          </div>
          <div className="pd-stat-item">
            <span className="pd-stat-label">Main Market</span>
            <span className="pd-stat-value">&nbsp;</span>
          </div>
          <div className="pd-stat-item">
            <span className="pd-stat-label">Average Lead Time</span>
            <span className="pd-stat-value">&nbsp;</span>
          </div>
        </div>
        <div className="pd-supplier-card-divider"></div>
        <div className="pd-supplier-card-actions">
          <Link to={`/seller/${seller.slug || seller.id}`} className="pd-company-profile-btn">COMPANY PROFI...</Link>
          <Link to={`/seller/${seller.slug || seller.id}`} className="pd-visit-store-btn">VISIT STORE</Link>
        </div>
      </div>
    </div>
  );

  const renderReviewCard = (review) => (
    <div key={review.id} className="pd-review-card">
      <div className="pd-review-top">
        <img src={review.avatar} alt={review.name} className="pd-review-avatar" />
        <div>
          <h4 className="pd-review-name">{review.name}</h4>
          <span className="pd-review-location">{review.location}</span>
        </div>
      </div>
      <div className="pd-review-body">
        <div className="pd-review-stars">
          {[1,2,3,4,5].map(i => (
            <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i <= review.rating ? '#FFBD00' : '#D1D6DB'}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          ))}
          <span className="pd-review-rating-num">{review.rating}/5</span>
        </div>
        <span className="pd-review-date">{review.date}</span>
        {review.comment && <p className="pd-review-comment">{review.comment}</p>}
      </div>
    </div>
  );

  return (
    <div className="product-details-page">
      <Header />

      <main className="pd-main">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}><p>Loading product details...</p></div>
        ) : notFound ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <h2 style={{ marginBottom: '12px', color: '#333' }}>Product Not Found</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>This product may have been removed or is no longer available.</p>
            <Link to="/products" style={{ color: '#E8B931', textDecoration: 'none', fontWeight: 600 }}>Browse Products</Link>
          </div>
        ) : (
        <>
          {/* Breadcrumb */}
          <div className="pd-breadcrumb">
            <Link to="/" className="pd-bc-link">Home</Link>
            <span className="pd-bc-sep">/</span>
            <Link to="/products" className="pd-bc-link">All Categories</Link>
            <span className="pd-bc-sep">/</span>
            <Link to={`/products/${product?.category?.slug || ''}`} className="pd-bc-link">{product?.category?.name || 'Category'}</Link>
            <span className="pd-bc-sep">/</span>
            <span className="pd-bc-current">{(product?.name || product?.title || '').substring(0, 80)}{(product?.name || '').length > 80 ? '...' : ''}</span>
          </div>

          {/* Hero: Image + Info + Sidebar */}
          <div className="pd-hero">
            <div className="pd-hero-left">
              {/* Product Image */}
              <div className="pd-image-col">
                <div className="pd-main-image" onClick={() => openLightbox(thumbnails.indexOf(mainImage) >= 0 ? thumbnails.indexOf(mainImage) : 0)}>
                  <img src={mainImage || product?.image} alt={product?.name || product?.title} />
                  {thumbnails.length > 1 && (
                    <>
                      <button className="pd-nav-btn pd-nav-prev" onClick={() => {
                        const idx = thumbnails.indexOf(mainImage);
                        setMainImage(thumbnails[idx > 0 ? idx - 1 : thumbnails.length - 1]);
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <button className="pd-nav-btn pd-nav-next" onClick={() => {
                        const idx = thumbnails.indexOf(mainImage);
                        setMainImage(thumbnails[idx < thumbnails.length - 1 ? idx + 1 : 0]);
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <div className="pd-image-counter">{thumbnails.indexOf(mainImage) + 1} / {thumbnails.length}</div>
                    </>
                  )}
                </div>
                <div className="pd-thumbnails">
                  {thumbnails.map((thumb, idx) => (
                    <img
                      key={idx}
                      src={thumb}
                      alt={`View ${idx + 1}`}
                      className={`pd-thumb ${mainImage === thumb ? 'active' : ''}`}
                      onClick={() => setMainImage(thumb)}
                    />
                  ))}
                </div>
              </div>

              {/* Product Info */}
              <div className="pd-info-col">
                <div className="pd-title-row">
                  <h1 className="pd-title">{product?.name || product?.title}</h1>
                  <button
                    className={`pd-favourite-btn ${isFavorited ? 'favorited' : ''} ${isTogglingFav ? 'toggling' : ''}`}
                    onClick={handleToggleFavourite}
                    disabled={isTogglingFav}
                    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <svg width="16" height="22" viewBox="0 0 14 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0C12.3978 0 12.7792 0.158149 13.0605 0.439453C13.3419 0.720758 13.5 1.10218 13.5 1.5V18C13.4998 18.1337 13.4634 18.2647 13.3955 18.3799C13.3276 18.495 13.2302 18.5905 13.1133 18.6553C12.9964 18.72 12.864 18.7524 12.7305 18.7487C12.5969 18.745 12.4664 18.7055 12.3535 18.6347L6.75 14.9297L1.14648 18.6347C1.03357 18.7055 0.90306 18.745 0.769531 18.7487C0.636003 18.7524 0.503552 18.72 0.38665 18.6553C0.269749 18.5905 0.172363 18.495 0.104492 18.3799C0.0366211 18.2647 0.000366211 18.1337 0 18V1.5C0 1.10218 0.158149 0.720758 0.439453 0.439453C0.720758 0.158149 1.10218 0 1.5 0H12Z" fill={isFavorited ? "#1F396C" : "#969696"}/>
                    </svg>
                  </button>
                </div>
                <div className="pd-rating-row">
                  <span className="pd-rating-text">Rating</span>
                  <span className="pd-reviews-link">{reviewCount} Reviews</span>
                </div>
                <div className="pd-badges-row">
                  <span className="pd-views-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#4CAF50"/></svg>
                    {product?.views || 0} Views
                  </span>
                  <span className="pd-inquired-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#FF6B00"/></svg>
                    {product?.inquiries || 0} People inquired
                  </span>
                </div>
                <div className="pd-price-section">
                  <div className="pd-price-label">Price</div>
                  <div className="pd-price-range">
                    <span className="pd-price-value">{formatPrice(product?.priceMin || 0)}</span>
                    {product?.priceMax > 0 && product?.priceMax !== product?.priceMin && (
                      <span className="pd-price-max"> - {formatPrice(product.priceMax)}</span>
                    )}
                    {product?.unit && <span className="pd-price-unit">/ {product.unit}</span>}
                  </div>
                </div>
                <div className="pd-divider"></div>
                <div className="pd-key-details">
                  {product?.moq && (
                    <div className="pd-key-detail-row">
                      <span className="pd-key-label">Min. Order</span>
                      <span className="pd-key-value">{product.moq}</span>
                    </div>
                  )}
                  {product?.category?.name && (
                    <div className="pd-key-detail-row">
                      <span className="pd-key-label">Category</span>
                      <span className="pd-key-value">
                        {product.category.name}
                        {product?.subcategory?.name && ` > ${product.subcategory.name}`}
                      </span>
                    </div>
                  )}
                  {product?.brand?.name && (
                    <div className="pd-key-detail-row">
                      <span className="pd-key-label">Brand</span>
                      <span className="pd-key-value">{product.brand.name}</span>
                    </div>
                  )}
                  <div className="pd-key-detail-row">
                    <span className="pd-key-label">Availability</span>
                    <span className="pd-key-value" style={{ color: product?.status === 'published' ? '#006928' : '#888' }}>
                      {product?.status === 'published' ? 'In Stock' : 'Unavailable'}
                    </span>
                  </div>
                </div>
                <div className="pd-divider"></div>
                <div className="pd-action-buttons">
                  <button type="button" className="pd-add-to-cart-btn" onClick={handleAddToCart}>
                    {product?.id && isInInquiry(product.id) ? 'In cart — add more' : 'Add to cart'}
                  </button>
                  <button className="pd-inquire-now-btn" onClick={handleRequestInquire}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#fff"/></svg>
                    INQUIRE NOW
                  </button>
                  <button className="pd-rfq-btn" onClick={() => navigate('/rfq')}>RFQ</button>
                  <button
                    className={`pd-compare-btn ${inCompare ? 'active' : ''}`}
                    onClick={() => {
                      if (inCompare) {
                        removeFromCompare(product.id);
                      } else {
                        const seller = product.seller || {};
                        addToCompare({
                          id: product.id,
                          name: product.name || product.description,
                          image: product.image,
                          priceMin: product.priceMin,
                          priceMax: product.priceMax,
                          priceUnit: product.priceUnit,
                          pricePer: product.pricePer,
                          moq: product.moq,
                          description: product.description,
                          shortDescription: product.shortDescription,
                          overview: product.overview || product.longDescription,
                          keyFeatures: product.keyFeatures,
                          companyName: seller.name || product.companyName || '',
                          companyLogo: seller.logo || product.companyLogo || '',
                          country: seller.country || product.country || '',
                          countryFlag: seller.countryFlag || product.countryFlag || '',
                          businessType: seller.businessType || product.businessType || '',
                          categoryName: product.category?.name || '',
                          subcategoryName: product.subcategory?.name || '',
                        });
                      }
                    }}
                    title={inCompare ? 'Remove from compare' : isFull ? 'Compare list full (max 2)' : 'Add to compare'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M21 3l-7 7"/><path d="M3 3l7 7"/><path d="M16 21h5v-5"/><path d="M8 21H3v-5"/><path d="M21 21l-7-7"/><path d="M3 21l7-7"/></svg>
                    {inCompare ? 'COMPARING' : 'COMPARE'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            {renderSupplierSidebar()}
          </div>

          {/* You May Also Like */}
          {relatedProducts.length > 0 && (
            <div className="pd-also-like-section">
              <h2 className="pd-also-like-title">You may also like</h2>
              <div className="pd-also-like-grid">
                {relatedProducts.slice(0, 5).map(item => {
                  let itemImage = item.image;
                  if (item.images) {
                    try {
                      const imgs = typeof item.images === 'string' ? JSON.parse(item.images) : item.images;
                      if (imgs && imgs.length > 0) itemImage = imgs[0];
                    } catch (e) {}
                  }
                  return (
                    <div key={item.id} className="pd-also-like-card" onClick={() => navigate(`/product/${item.id}`)}>
                      <div className="pd-also-like-img-wrap">
                        <img src={itemImage || '/uploads/builder-images/1c71bdd709a90ef879ec030e5f88229d06ab1391_w800.png'} alt={item.name} />
                      </div>
                      <div className="pd-also-like-info">
                        <p className="pd-also-like-name">{item.name}</p>
                        <p className="pd-also-like-price">{item.priceMin || 0}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabs + Content + Sidebar Layout */}
          <div className="pd-tabs-wrapper">
            <div className="pd-tabs-content-area">
              {/* Main Tabs */}
              <div className="pd-main-tabs">
                {['productDetails', 'companyProfile', 'sellerReview', 'productReview'].map(tab => (
                  <button
                    key={tab}
                    className={`pd-main-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => { setActiveTab(tab); if (tab === 'productDetails') setActiveSubTab('details'); }}
                  >
                    {tab === 'productDetails' ? 'Product details' : tab === 'companyProfile' ? 'Company Profile' : tab === 'sellerReview' ? 'Seller Review' : 'Product Review'}
                  </button>
                ))}
              </div>

              {/* Sub-tabs (only for Product details) */}
              {activeTab === 'productDetails' && (
                <div className="pd-sub-tabs">
                  {['details', 'packaging', 'faq', 'warranty'].map(sub => (
                    <button
                      key={sub}
                      className={`pd-sub-tab ${activeSubTab === sub ? 'active' : ''}`}
                      onClick={() => setActiveSubTab(sub)}
                    >
                      {sub === 'details' ? 'Product details' : sub === 'packaging' ? 'Packaging & Delivery' : sub === 'faq' ? 'FAQ' : 'Warranty'}
                    </button>
                  ))}
                </div>
              )}

              {/* Tab Content */}
              <div className="pd-tab-content">
                {activeTab === 'productDetails' && activeSubTab === 'details' && (
                  <>
                    {/* Overview section with key product attributes */}
                    <div className="pd-overview-section">
                      <h3 className="pd-section-heading">Product Overview</h3>
                      <div className="pd-overview-grid">
                        {product?.name && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Product Name</span>
                            <span className="pd-overview-value">{product.name}</span>
                          </div>
                        )}
                        {product?.category?.name && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Category</span>
                            <span className="pd-overview-value">{product.category.name}</span>
                          </div>
                        )}
                        {product?.subcategory?.name && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Subcategory</span>
                            <span className="pd-overview-value">{product.subcategory.name}</span>
                          </div>
                        )}
                        {product?.brand?.name && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Brand</span>
                            <span className="pd-overview-value">{product.brand.name}</span>
                          </div>
                        )}
                        <div className="pd-overview-item">
                          <span className="pd-overview-label">Price</span>
                          <span className="pd-overview-value">
                            {formatPrice(product?.priceMin || 0)}
                            {product?.priceMax > 0 && product?.priceMax !== product?.priceMin ? ` - ${formatPrice(product.priceMax)}` : ''}
                            {product?.unit ? ` / ${product.unit}` : ''}
                          </span>
                        </div>
                        {product?.moq && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Min. Order</span>
                            <span className="pd-overview-value">{product.moq}</span>
                          </div>
                        )}
                        {(seller?.name || seller?.companyName) && (
                          <div className="pd-overview-item">
                            <span className="pd-overview-label">Supplier</span>
                            <span className="pd-overview-value">{seller.name || seller.companyName}</span>
                          </div>
                        )}
                        <div className="pd-overview-item">
                          <span className="pd-overview-label">Availability</span>
                          <span className="pd-overview-value" style={{ color: product?.status === 'published' ? '#006928' : '#888' }}>
                            {product?.status === 'published' ? 'In Stock' : 'Unavailable'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Full Description */}
                    {product?.description && (
                      <div className="pd-description-block">
                        <h3 className="pd-section-heading">Description</h3>
                        <div className="pd-description-text" dangerouslySetInnerHTML={{ __html: product.description }} />
                      </div>
                    )}

                    {/* Specifications Table */}
                    {allSpecs.length > 0 && (
                    <>
                    <h3 className="pd-section-heading" style={{ marginTop: 24 }}>Specifications</h3>
                    <table className="pd-specs-table">
                      <tbody>
                        {Array.from({ length: Math.ceil(allSpecs.length / 2) }, (_, i) => (
                          <tr key={i}>
                            <td className="pd-spec-label">{allSpecs[i * 2]?.label}</td>
                            <td className="pd-spec-value">{allSpecs[i * 2]?.value}</td>
                            {allSpecs[i * 2 + 1] ? (
                              <>
                                <td className="pd-spec-label">{allSpecs[i * 2 + 1].label}</td>
                                <td className="pd-spec-value">{allSpecs[i * 2 + 1].value}</td>
                              </>
                            ) : (
                              <>
                                <td className="pd-spec-label"></td>
                                <td className="pd-spec-value"></td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </>
                    )}

                    {/* Image Gallery */}
                    {thumbnails.length > 1 && (
                      <>
                        <h3 className="pd-section-heading" style={{ marginTop: 24 }}>Product Images</h3>
                        <div className="pd-gallery-grid">
                          {thumbnails.map((img, idx) => (
                            <div key={idx} className="pd-gallery-item" onClick={() => openLightbox(idx)}>
                              <img src={img} alt={`${product?.name || 'Product'} - Image ${idx + 1}`} />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}

                {activeTab === 'productDetails' && activeSubTab === 'packaging' && (
                  <>
                    <h3 className="pd-section-heading">Packaging & Delivery</h3>
                    <table className="pd-package-table">
                      <tbody>
                        <tr><td className="pd-pkg-label">Selling Units:</td><td className="pd-pkg-value">{product?.sellingUnits || ''}</td></tr>
                        <tr><td className="pd-pkg-label">Logistic Mode</td><td className="pd-pkg-value">{product?.logisticMode || ''}</td></tr>
                        <tr><td className="pd-pkg-label">Shipping Port</td><td className="pd-pkg-value">{product?.shippingPort || ''}</td></tr>
                        <tr><td className="pd-pkg-label">Package Description</td><td className="pd-pkg-value">{product?.packageDescription || ''}</td></tr>
                        <tr><td className="pd-pkg-label">Packaging Photos</td><td className="pd-pkg-value">{product?.packagingPhotos ? <img src={product.packagingPhotos} alt="Packaging" style={{ maxWidth: 200 }} /> : ''}</td></tr>
                      </tbody>
                    </table>
                    <div className="pd-divider" style={{ margin: '24px 0' }}></div>
                    <h3 className="pd-section-heading">Product Information</h3>
                    <div className="pd-product-info-text">
                      <p>{product?.description || 'No additional product information available.'}</p>
                    </div>
                    {/* Product images */}
                    {thumbnails.length > 0 && (
                      <div className="pd-gallery-grid">
                        {thumbnails.map((img, idx) => (
                          <div key={idx} className="pd-gallery-item" onClick={() => openLightbox(idx)}>
                            <img src={img} alt={`Product ${idx + 1}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'productDetails' && activeSubTab === 'faq' && (
                  <div className="pd-faq-content">
                    <h3 className="pd-section-heading">FAQ</h3>
                    <p className="pd-empty-text">No FAQs available for this product.</p>
                  </div>
                )}

                {activeTab === 'productDetails' && activeSubTab === 'warranty' && (
                  <div className="pd-warranty-content">
                    <h3 className="pd-section-heading">Warranty</h3>
                    <p className="pd-empty-text">No warranty information available for this product.</p>
                  </div>
                )}

                {activeTab === 'companyProfile' && (
                  <div className="pd-company-profile-content">
                    <h3 className="pd-section-heading">Company Profile</h3>
                    <div className="pd-company-info-grid">
                      <div className="pd-company-row"><span className="pd-company-label">Company Name</span><span className="pd-company-val">{seller.name || seller.companyName}</span></div>
                      <div className="pd-company-row"><span className="pd-company-label">Business Type</span><span className="pd-company-val">{seller.businessType || 'Manufacturer'}</span></div>
                      <div className="pd-company-row"><span className="pd-company-label">Country</span><span className="pd-company-val">{seller.country}</span></div>
                      <div className="pd-company-row"><span className="pd-company-label">Member Since</span><span className="pd-company-val">{seller.memberYears || 1} Year(s)</span></div>
                    </div>
                  </div>
                )}

                {activeTab === 'sellerReview' && (
                  <div className="pd-seller-review-content">
                    <h3 className="pd-section-heading">Seller Reviews</h3>
                    {reviews.length > 0 ? (
                      <div className="pd-reviews-list">
                        {reviews.map(review => renderReviewCard(review))}
                      </div>
                    ) : (
                      <p className="pd-empty-text">No seller reviews yet.</p>
                    )}
                  </div>
                )}

                {activeTab === 'productReview' && (
                  <div className="pd-product-review-content">
                    <h3 className="pd-section-heading">Product Reviews</h3>
                    <p className="pd-empty-text">No product reviews yet.</p>
                  </div>
                )}

                {/* Contact form - always visible at bottom of tab content */}
                <div className="pd-contact-form-section">
                  <h2 className="pd-contact-form-title">Send your message to this supplier</h2>
                  <div className="pd-contact-form">
                    <div className="pd-form-row">
                      <label className="pd-form-label">To:</label>
                      <span className="pd-form-value">{seller.name || seller.companyName}</span>
                    </div>
                    <div className="pd-form-row">
                      <label className="pd-form-label">*Email:</label>
                      <input type="email" className="pd-form-input" placeholder="Enter Email Address" value={inquiryForm.email} onChange={e => setInquiryForm(p => ({ ...p, email: e.target.value }))} />
                    </div>
                    <div className="pd-form-row">
                      <label className="pd-form-label">*Mobile:</label>
                      <input type="tel" className="pd-form-input" placeholder="Enter Mobile Number" value={inquiryForm.mobile} onChange={e => setInquiryForm(p => ({ ...p, mobile: e.target.value }))} />
                    </div>
                    <div className="pd-form-row pd-form-row-message">
                      <label className="pd-form-label">*Message:</label>
                      <div className="pd-message-wrapper">
                        <textarea className="pd-form-textarea" placeholder="Enter your inquiry details such as product name, color, size, MOQ, FOB, etc." value={inquiryForm.message} onChange={e => setInquiryForm(p => ({ ...p, message: e.target.value }))} />
                        <div className="pd-message-tip">
                          <p>For better quotations, include:</p>
                          <p>- A self introduction</p>
                          <p>- Special requests, if any</p>
                        </div>
                      </div>
                    </div>
                    <p className="pd-form-hint">Your message must be between 20-8000 characters</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky sidebar in tabs area too */}
            {renderSupplierSidebar()}
          </div>
        </>
        )}
      </main>

      {/* Image Lightbox */}
      {lightboxOpen && thumbnails.length > 0 && (
        <div className="pd-lightbox-overlay" onClick={closeLightbox}>
          <div className="pd-lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="pd-lightbox-close" onClick={closeLightbox}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {thumbnails.length > 1 && (
              <button className="pd-lightbox-nav pd-lightbox-prev" onClick={lightboxPrev}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            )}
            <img src={thumbnails[lightboxIndex]} alt={`${product?.name || 'Product'} - Full size ${lightboxIndex + 1}`} />
            {thumbnails.length > 1 && (
              <button className="pd-lightbox-nav pd-lightbox-next" onClick={lightboxNext}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            )}
            {thumbnails.length > 1 && (
              <>
                <div className="pd-lightbox-counter">{lightboxIndex + 1} / {thumbnails.length}</div>
                <div className="pd-lightbox-thumbs">
                  {thumbnails.map((thumb, idx) => (
                    <img
                      key={idx}
                      src={thumb}
                      alt={`Thumb ${idx + 1}`}
                      className={`pd-lightbox-thumb ${lightboxIndex === idx ? 'active' : ''}`}
                      onClick={() => setLightboxIndex(idx)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />

      <ChatModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        seller={{ id: product?.seller?.id || seller.id, name: seller.name || seller.companyName, logo: seller.logo, isVerified: seller.isVerified }}
      />
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => { setShowUpgradeModal(false); setUpgradeMessage(''); }}
        feature={upgradeFeature} message={upgradeMessage}
        currentPlan={planDisplayName || 'Basic'} requiredPlan="GCC Business"
      />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onSwitchToRegister={() => setShowLoginModal(false)} />

      {product && (
        <MobileProductVariationsModal
          open={variationsOpen}
          onClose={() => setVariationsOpen(false)}
          product={product}
          thumbUrl={thumbnails[0] || mainImage || LOGO_PH}
          formatPrice={formatPrice}
          deliverCode={deliverCode}
          onConfirmAdd={handleVariationsConfirm}
        />
      )}

      {favToast && (
        <div className={`fav-toast fav-toast--${favToast.type}`}>{favToast.msg}</div>
      )}
    </div>
  );
};

export default ProductDetailsPage;
